/**
 * E-Rechnung nach EN 16931 in der Syntax UN/CEFACT Cross Industry Invoice (CII), wahlweise mit der deutschen
 * Spezifikation XRechnung 3.0 (Pflicht beim Versand an Unternehmen in Deutschland ab 2027/2028, Empfang seit 2025)
 * oder als reines EN-16931-Profil (so steckt es auch in ZUGFeRD/Factur-X). Rein: das Eingabemodell kommt aus den
 * festgeschriebenen Rechnungsdaten, dieselben Werte wie auf dem PDF. Vor dem Erzeugen meldet checkEInvoice, was fehlt.
 *
 * Belegarten: 380 Rechnung, 381 Rechnungskorrektur/Stornorechnung (Beträge positiv, Bezug auf die Originalrechnung),
 * 389 Gutschrift im Gutschriftverfahren (Aussteller ist der Leistungsempfänger).
 *
 * K-417c Gutschrift (selfBilling): Die Rollen der Norm bleiben am Leistungsaustausch, nicht am Ersteller des Belegs. Verkäufer
 * (BG-4, Seller) ist der leistende Creator mit seiner USt-IdNr. bzw. Steuernummer (BT-31/BT-32) und seiner Bankverbindung
 * (BG-17: die Agentur zahlt an ihn), Käufer (BG-7, Buyer) ist die Agentur, die die Gutschrift ausstellt. Kennzeichnung als
 * Gutschrift über die Rechnungsart 389 (UStAE 14.5 Abs. 24 i. d. F. des BMF-Schreibens vom 15.10.2025: „bei E-Rechnungen
 * beispielsweise durch die Auswahl des entsprechenden Rechnungstyps“) und zusätzlich als Hinweis „Gutschrift“ (BT-22), wie das
 * Pflichtwort auf dem PDF (§ 14 Abs. 4 Satz 1 Nr. 10 UStG). Fehlermeldungen nennen dann „Creator“ und „Agentur“ statt
 * „Aussteller“ und „Empfänger“, weil die Agentur hier den Beleg ausstellt.
 *
 * Vorzeichen: Alle Beträge werden relativ zum Beleg geführt (s = Vorzeichen der Nettosumme). Eine Stornorechnung mit
 * gespeicherten negativen Beträgen erscheint positiv, eine Rabatt- oder Abzugszeile bleibt negativ (auch im Storno,
 * dort gespiegelt). Der Nettopreis ist nie negativ (BR-27); eine Abzugszeile trägt eine negative Menge.
 */
import type { Cents, Currency, IsoDate } from "./types.js";
import { checkVatIdFormat, taxModeFromDb, type TaxMode } from "./tax.js";

export type EInvoiceProfile = "xrechnung" | "en16931";
export type EInvoiceTypeCode = 380 | 381 | 389;
/** Steuerkategorien nach UNTDID 5305, soweit das Produkt sie braucht */
export type EInvoiceTaxCategory = "S" | "Z" | "E" | "AE" | "O";

export const EINVOICE_GUIDELINE: Readonly<Record<EInvoiceProfile, string>> = {
  xrechnung: "urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung_3.0",
  en16931: "urn:cen.eu:en16931:2017",
};
const PEPPOL_PROCESS = "urn:fdc:peppol.eu:2017:poacc:billing:01:1.0";

export interface EInvoiceParty {
  name: string;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  /** ISO 3166-1 alpha-2 */
  country: string;
  vatId?: string | null;
  taxNumber?: string | null;
  /** Handelsregister o. Ä. (BT-30) */
  registration?: string | null;
  /** elektronische Adresse (BT-34 / BT-49), hier eine E-Mail-Adresse */
  email?: string | null;
}

export interface EInvoiceContact {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface EInvoiceLine {
  name: string;
  quantity: number;
  /** UN/ECE Rec 20, Standard C62 (Stück) */
  unitCode?: string;
  netPriceCents: Cents;
  netAmountCents: Cents;
  ratePercent: number;
}

export interface EInvoiceInput {
  profile: EInvoiceProfile;
  typeCode: EInvoiceTypeCode;
  number: string;
  issueDate: IsoDate;
  dueDate?: IsoDate | null;
  currency: Currency;
  /** Leistungszeitraum (BG-14) oder Leistungsdatum (BT-72) */
  servicePeriod?: { from: IsoDate; to: IsoDate } | null;
  deliveryDate?: IsoDate | null;
  /** Käuferreferenz (BT-10), z. B. Leitweg-ID; in XRechnung Pflicht */
  buyerReference?: string | null;
  /** Bestellnummer der Marke (BT-13) */
  orderReference?: string | null;
  /** Bezug bei Rechnungskorrektur/Storno (BT-25/26) */
  precedingInvoice?: { number: string; date?: IsoDate | null } | null;
  seller: EInvoiceParty;
  sellerContact?: EInvoiceContact | null;
  buyer: EInvoiceParty;
  lines: readonly EInvoiceLine[];
  /** tax_mode wie in der Datenbank (standard, small_business, reverse_charge_eu …) */
  taxMode: string | null;
  /** Hinweistext auf dem Beleg (Kleinunternehmer, Reverse Charge …); wird auch Befreiungsgrund */
  taxNotice?: string | null;
  totals: { netCents: Cents; taxCents: Cents; grossCents: Cents; prepaidCents?: Cents };
  /**
   * K-309: Steuerwährung (BT-6) mit Gesamtsteuer in dieser Währung (BT-111), wenn die Rechnung in fremder Währung deutsche bzw.
   * österreichische Umsatzsteuer trägt: die beim Festschreiben eingefrorene „Umsatzsteuer in EUR“ (0267/0269), wie auf dem PDF.
   * Nur, wenn sie von der Rechnungswährung abweicht (PEPPOL-EN16931-R005); gleiches Vorzeichen wie BT-110.
   */
  taxCurrency?: { code: Currency; taxCents: Cents } | null;
  payment?: EInvoicePayment | null;
  paymentTerms?: string | null;
  notes?: readonly string[];
  /**
   * K-417c: Gutschrift im Gutschriftverfahren (bzw. deren Storno). Verkäufer = leistender Creator, Käufer = ausstellende Agentur;
   * ändert nur die Wortwahl der Prüfmeldungen (Creator/Agentur statt Aussteller/Empfänger), nicht die Regeln.
   */
  selfBilling?: boolean;
  /** Sprache des Belegs (document_language) für Klartexte im Datensatz, etwa die Zahlungsanweisung ohne IBAN; leer = Deutsch */
  document?: { language?: string | null } | null;
}

/**
 * Zahlungsangaben (BG-16/BG-17): Konto des Verkäufers. In der Gutschrift (K-417c) ist das der Auszahlungsweg des Creators aus dem
 * eingefrorenen Stand (issuer_snapshot.bank: method sepa, swift, paypal oder wise; IBAN bzw. Kontonummer; Kennung bei PayPal/Wise).
 */
export interface EInvoicePayment {
  iban?: string | null;
  bic?: string | null;
  accountName?: string | null;
  /** Auszahlungsweg (talent_payout_details.payout_method): sepa, swift, paypal, wise; leer = Überweisung */
  method?: string | null;
  /** Kontonummer ohne IBAN (SWIFT-Überweisung außerhalb des IBAN-Raums) */
  account?: string | null;
  /** Kennung beim Zahlungsdienst (PayPal, Wise): die E-Mail-Adresse des Kontos */
  email?: string | null;
}

/** Zahlungsart nach UNTDID 4461, soweit das Produkt sie braucht, mit dem Inhalt von BG-16/BG-17 */
export interface EInvoicePaymentMeans {
  /** 58 SEPA-Überweisung, 30 Überweisung (SWIFT bzw. ohne IBAN), 68 Online-Zahlungsdienst, 1 nicht festgelegt */
  code: "58" | "30" | "68" | "1";
  /** Zahlungsanweisung im Klartext (BT-82), nur ohne IBAN */
  text: string | null;
  /** Konto (BT-84): IBAN oder, ohne IBAN, die Kontonummer */
  iban: string | null;
  accountId: string | null;
  accountName: string | null;
  bic: string | null;
}

/**
 * Zahlungsart der E-Rechnung (Entscheidung Inhaber 16.10.2026, Punkt 3). Mit IBAN: SEPA-Überweisung 58 (bei SWIFT 30) mit dem Konto
 * (BG-17), wie bisher. Ohne IBAN nur in der Gutschrift, in der der Creator sein Geld auch per PayPal oder Wise bekommt: PayPal bzw.
 * Wise als Online-Zahlungsdienst 68 mit der Kennung des Kontos als Zahlungsanweisung (BT-82), eine Kontonummer ohne IBAN als
 * Überweisung 30 (BT-84), sonst 1 „nicht festgelegt“ mit „Auszahlung auf dem vereinbarten Weg“ wie auf dem PDF. Die Gutschrift geht
 * so trotzdem als E-Rechnung. Rechnungen der Agentur an Marken brauchen weiter eine IBAN (BR-DE-1 „Zahlungsangaben (IBAN) fehlen.“).
 */
export function paymentMeansOf(input: Pick<EInvoiceInput, "payment" | "selfBilling" | "document">): EInvoicePaymentMeans | null {
  const p = input.payment ?? null;
  const method = clean(p?.method).toLowerCase();
  const iban = cleanId(p?.iban);
  const accountName = clean(p?.accountName) || null;
  const bic = cleanId(p?.bic) || null;
  if (iban) return { code: method === "swift" ? "30" : "58", text: null, iban, accountId: null, accountName, bic };
  if (input.selfBilling !== true) return null;
  const en = input.document?.language === "en";
  if (method === "paypal" || method === "wise") {
    const name = method === "paypal" ? "PayPal" : "Wise";
    const id = clean(p?.email);
    return { code: "68", text: id ? `${name}: ${id}` : name, iban: null, accountId: null, accountName, bic: null };
  }
  const account = cleanId(p?.account);
  if (account) return { code: "30", text: en ? "Bank transfer" : "Überweisung", iban: null, accountId: account, accountName, bic };
  return { code: "1", text: en ? "Payout via the agreed method" : "Auszahlung auf dem vereinbarten Weg", iban: null, accountId: null, accountName: null, bic: null };
}

export interface EInvoiceProblem {
  /** Regelkennung der Norm bzw. der XRechnung (BR-…, BR-DE-…) */
  rule: string;
  /** verständliche Beschreibung, was fehlt */
  text: string;
  /**
   * K-433: wessen Angabe fehlt (Verkäufer BG-4 bzw. Käufer BG-7), damit der Dialog zur richtigen Stelle führt: in der Gutschrift
   * ist der Verkäufer der Creator (Creator-Profil) und der Käufer die Agentur (Einstellungen · Firma), sonst umgekehrt.
   */
  party?: "seller" | "buyer";
}

/** Steuerkategorie aus dem Steuerfall des Belegs */
export function taxCategoryFor(taxMode: string | null, ratePercent: number): EInvoiceTaxCategory {
  const m: TaxMode | null = taxModeFromDb(taxMode);
  switch (m) {
    case "SMALL_BUSINESS": return "E";
    case "REVERSE_CHARGE_EU":
    case "REVERSE_CHARGE_DOMESTIC": return "AE";
    case "NOT_TAXABLE_ABROAD":
    case "NOT_TAXABLE_DAMAGES": return "O";
    default: return ratePercent > 0 ? "S" : "Z";
  }
}

const EXEMPTION_DEFAULT: Record<Exclude<EInvoiceTaxCategory, "S" | "Z">, string> = {
  E: "Steuerbefreit: Kleinunternehmerregelung",
  AE: "Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)",
  O: "Nicht im Inland steuerbare Leistung",
};

const clean = (s: string | null | undefined) => (s ?? "").replace(/\s+/g, " ").trim();
const cleanId = (s: string | null | undefined) => (s ?? "").replace(/\s+/g, "").toUpperCase();
/** Betrag mit Vorzeichen, zwei Nachkommastellen (ohne „-0.00“) */
const amount = (c: number) => (c === 0 ? 0 : c / 100).toFixed(2);
/**
 * Vorzeichen des Belegs: nur eine Stornorechnung (381) mit negativ gespeicherten Beträgen wird positiv geführt. Eine Rechnung (380)
 * behält ihr Vorzeichen, sonst forderte das XML einen Betrag ein, den das PDF gutschreibt (Praxistest G-004).
 */
const docSign = (input: EInvoiceInput): 1 | -1 =>
  input.typeCode === 381 ? ((Math.sign(input.totals.netCents) || Math.sign(input.totals.grossCents) || 1) as 1 | -1) : 1;
const d102 = (iso: string) => iso.slice(0, 10).replace(/-/g, "");
const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const rate = (r: number) => (Number.isInteger(r) ? String(r) : r.toFixed(2).replace(/0+$/, "").replace(/\.$/, ""));

/** Summen je Steuerkategorie und Satz (BG-23) */
export function taxBreakdown(input: EInvoiceInput): { category: EInvoiceTaxCategory; ratePercent: number; basisCents: Cents; taxCents: Cents }[] {
  const s = docSign(input);
  const groups = new Map<string, { category: EInvoiceTaxCategory; ratePercent: number; basisCents: number }>();
  for (const l of input.lines) {
    const category = taxCategoryFor(input.taxMode, l.ratePercent);
    const r = category === "S" ? l.ratePercent : 0;
    const key = `${category}:${r}`;
    const g = groups.get(key) ?? { category, ratePercent: r, basisCents: 0 };
    // vorzeichenrichtig: eine Rabattzeile mindert die Grundlage (BR-S-08)
    g.basisCents += s * l.netAmountCents;
    groups.set(key, g);
  }
  const list = [...groups.values()];
  // Steuer je Gruppe; Rundungsrest auf die größte Gruppe, damit die Summe dem Beleg entspricht
  const out = list.map((g) => ({ ...g, taxCents: g.category === "S" ? Math.round((g.basisCents * g.ratePercent) / 100) : 0 }));
  const want = s * input.totals.taxCents;
  const got = out.reduce((a, g) => a + g.taxCents, 0);
  const biggest = out.filter((g) => g.category === "S").sort((a, b) => b.basisCents - a.basisCents)[0];
  if (biggest && got !== want && Math.abs(got - want) <= out.length) biggest.taxCents += want - got;
  return out;
}

/** Prüft die Pflichtangaben der Norm und (Profil xrechnung) der deutschen Geschäftsregeln; leer = in Ordnung. */
export function checkEInvoice(input: EInvoiceInput): EInvoiceProblem[] {
  const p: EInvoiceProblem[] = [];
  const x = input.profile === "xrechnung";
  const need = (ok: unknown, rule: string, text: string, party?: "seller" | "buyer") => { if (!ok) p.push(party ? { rule, text, party } : { rule, text }); };
  // K-417c: in der Gutschrift ist der Verkäufer der Creator und der Käufer die (ausstellende) Agentur
  const sb = input.selfBilling === true;
  const S = sb ? "des Creators" : "des Ausstellers";
  const B = sb ? "der Agentur" : "des Empfängers";
  need(clean(input.number), "BR-02", sb ? "Gutschriftnummer fehlt." : "Rechnungsnummer fehlt.");
  need(/^\d{4}-\d{2}-\d{2}/.test(input.issueDate), "BR-03", sb ? "Datum der Gutschrift fehlt." : "Rechnungsdatum fehlt.");
  need(clean(input.seller.name), "BR-06", `Name ${S} fehlt.`, "seller");
  need(clean(input.buyer.name), "BR-07", `Name ${B} fehlt.`, "buyer");
  need(/^[A-Z]{2}$/.test(cleanId(input.seller.country)), "BR-09", `Land ${S} fehlt.`, "seller");
  need(/^[A-Z]{2}$/.test(cleanId(input.buyer.country)), "BR-11", `Land ${B} fehlt.`, "buyer");
  need(input.lines.length > 0, "BR-16", "Der Beleg hat keine Positionen.");
  input.lines.forEach((l, i) => {
    need(clean(l.name), "BR-25", `Position ${i + 1}: Bezeichnung fehlt.`);
    need(Number.isFinite(l.quantity) && l.quantity !== 0, "BR-22", `Position ${i + 1}: Menge fehlt.`);
  });
  // Vorzeichenrichtig: Rabatt- und Abzugszeilen mindern die Summe (BR-CO-10), auch im Storno
  const lineSum = input.lines.reduce((a, l) => a + l.netAmountCents, 0);
  need(lineSum === input.totals.netCents, "BR-CO-10", `Summe der Positionen (${amount(lineSum)}) weicht von der Nettosumme (${amount(input.totals.netCents)}) ab.`);
  need(input.totals.netCents + input.totals.taxCents === input.totals.grossCents, "BR-CO-15",
    "Netto plus Steuer ergibt nicht den Gesamtbetrag.");
  const cats = new Set(input.lines.map((l) => taxCategoryFor(input.taxMode, l.ratePercent)));
  if (cats.has("S") || cats.has("AE") || cats.has("Z")) {
    need(clean(input.seller.vatId) || clean(input.seller.taxNumber), "BR-S-02", `USt-IdNr. oder Steuernummer ${S} fehlt.`, "seller");
  }
  // K-434: die USt-IdNr. kommt ungeprüft aus dem festgeschriebenen Stand (z. B. „DE12345“); eine falsche Kennung im Datensatz
  // weist der Empfänger zurück. eInvoiceInputFrom lässt sie weg, wenn eine Steuernummer da ist; sonst hier im Klartext.
  const vatFehler = clean(input.seller.vatId) ? checkVatIdFormat(input.seller.vatId, cleanId(input.seller.country) || null) : null;
  if (vatFehler) {
    const erwartet = typeof vatFehler.params?.["expected"] === "string" ? ` (erwartet: ${vatFehler.params["expected"]})` : "";
    p.push({ rule: "BR-CO-09", text: `USt-IdNr. ${S} ungültig${erwartet}.`, party: "seller" });
  }
  if (cats.has("AE")) {
    need(clean(input.seller.vatId) || clean(input.seller.taxNumber), "BR-AE-02", `Bei Reverse Charge braucht ${sb ? "der Creator" : "der Aussteller"} eine USt-IdNr. oder Steuernummer.`, "seller");
    // BR-AE-02 verlangt bei jeder AE-Kategorie (auch § 13b im Inland) die USt-IdNr. (BT-48) oder Registerkennung (BT-47) des Käufers
    if (taxModeFromDb(input.taxMode) === "REVERSE_CHARGE_EU") need(clean(input.buyer.vatId), "BR-AE-02", `Bei Reverse Charge in der EU fehlt die USt-IdNr. ${B}.`, "buyer");
    else need(clean(input.buyer.vatId) || clean(input.buyer.registration), "BR-AE-02", `Bei Reverse Charge fehlt die USt-IdNr. oder Registernummer ${B}.`, "buyer");
  }
  if (input.taxCurrency) {
    // BR-53: mit Steuerwährung (BT-6) braucht der Beleg die Gesamtsteuer in dieser Währung (BT-111)
    need(Number.isFinite(input.taxCurrency.taxCents), "BR-53", "Umsatzsteuer in der Steuerwährung fehlt.");
    need(Math.sign(input.taxCurrency.taxCents) === Math.sign(input.totals.taxCents), "BR-53",
      "Umsatzsteuer in der Steuerwährung hat ein anderes Vorzeichen als die Umsatzsteuer der Rechnung.");
  }
  if (input.typeCode === 381) need(input.precedingInvoice?.number, "BR-55", sb ? "Beim Storno einer Gutschrift fehlt der Bezug auf die Gutschrift." : "Bei einer Stornorechnung fehlt der Bezug auf die Originalrechnung.");
  if (x) {
    need(clean(input.buyerReference), "BR-DE-15", "Käuferreferenz fehlt (Leitweg-ID der Behörde oder Referenz der Marke; ohne Vorgabe genügt die Rechnungsnummer).");
    need(clean(input.seller.city), "BR-DE-3", `Ort ${S} fehlt.`, "seller");
    need(clean(input.seller.postalCode), "BR-DE-4", `Postleitzahl ${S} fehlt.`, "seller");
    need(clean(input.buyer.city), "BR-DE-8", `Ort ${B} fehlt.`, "buyer");
    need(clean(input.buyer.postalCode), "BR-DE-9", `Postleitzahl ${B} fehlt.`, "buyer");
    need(clean(input.sellerContact?.name), "BR-DE-5", `Ansprechperson ${S} fehlt.`, "seller");
    need(clean(input.sellerContact?.phone), "BR-DE-6", sb ? "Telefonnummer des Creators fehlt (Creator-Profil, WhatsApp-Nummer)." : "Telefonnummer des Ausstellers fehlt.", "seller");
    need(clean(input.sellerContact?.email), "BR-DE-7", sb ? "E-Mail-Adresse des Creators fehlt." : "E-Mail-Adresse der Ansprechperson fehlt.", "seller");
    need(clean(input.seller.email), "BR-DE-SELLER-EAS", `Elektronische Adresse (E-Mail) ${S} fehlt.`, "seller");
    need(clean(input.buyer.email), "BR-DE-BUYER-EAS", `Elektronische Adresse (E-Mail) ${B} fehlt.`, "buyer");
    need(clean(input.seller.vatId) || clean(input.seller.taxNumber), "BR-DE-16", `USt-IdNr. oder Steuernummer ${S} fehlt.`, "seller");
    // BR-DE-1 gilt für jede XRechnung, auch für die Stornorechnung 381 (KoSIT-Prüfung 07.10.2026); in der Gutschrift ist es der
    // Auszahlungsweg des Creators: IBAN, sonst PayPal/Wise bzw. „vereinbarter Weg“ (paymentMeansOf, Entscheidung 16.10.2026)
    need(paymentMeansOf(input), "BR-DE-1", "Zahlungsangaben (IBAN) fehlen.", "seller");
  }
  return p;
}

function party(tag: string, pt: EInvoiceParty, contact: EInvoiceContact | null | undefined, omitVatId: boolean): string {
  const out: string[] = [`<ram:${tag}>`];
  // BR-CO-26 (KoSIT-Prüfung 07.10.2026): Ohne USt-IdNr. im Datensatz und ohne Register (Kleinunternehmer, „nicht steuerbar“)
  // braucht der Aussteller eine Kennung (BT-29); dafür steht die Steuernummer zusätzlich als Verkäuferkennung.
  if (tag === "SellerTradeParty" && (omitVatId || !clean(pt.vatId)) && !clean(pt.registration) && clean(pt.taxNumber)) {
    out.push(`<ram:ID>${esc(clean(pt.taxNumber))}</ram:ID>`);
  }
  out.push(`<ram:Name>${esc(clean(pt.name))}</ram:Name>`);
  if (clean(pt.registration)) out.push(`<ram:SpecifiedLegalOrganization><ram:ID>${esc(clean(pt.registration))}</ram:ID></ram:SpecifiedLegalOrganization>`);
  if (contact && (clean(contact.name) || clean(contact.phone) || clean(contact.email))) {
    out.push("<ram:DefinedTradeContact>");
    if (clean(contact.name)) out.push(`<ram:PersonName>${esc(clean(contact.name))}</ram:PersonName>`);
    if (clean(contact.phone)) out.push(`<ram:TelephoneUniversalCommunication><ram:CompleteNumber>${esc(clean(contact.phone))}</ram:CompleteNumber></ram:TelephoneUniversalCommunication>`);
    if (clean(contact.email)) out.push(`<ram:EmailURIUniversalCommunication><ram:URIID>${esc(clean(contact.email))}</ram:URIID></ram:EmailURIUniversalCommunication>`);
    out.push("</ram:DefinedTradeContact>");
  }
  out.push("<ram:PostalTradeAddress>");
  if (clean(pt.postalCode)) out.push(`<ram:PostcodeCode>${esc(clean(pt.postalCode))}</ram:PostcodeCode>`);
  if (clean(pt.street)) out.push(`<ram:LineOne>${esc(clean(pt.street))}</ram:LineOne>`);
  if (clean(pt.city)) out.push(`<ram:CityName>${esc(clean(pt.city))}</ram:CityName>`);
  out.push(`<ram:CountryID>${esc(cleanId(pt.country))}</ram:CountryID>`, "</ram:PostalTradeAddress>");
  if (clean(pt.email)) out.push(`<ram:URIUniversalCommunication><ram:URIID schemeID="EM">${esc(clean(pt.email))}</ram:URIID></ram:URIUniversalCommunication>`);
  if (!omitVatId && clean(pt.vatId)) out.push(`<ram:SpecifiedTaxRegistration><ram:ID schemeID="VA">${esc(cleanId(pt.vatId))}</ram:ID></ram:SpecifiedTaxRegistration>`);
  if (clean(pt.taxNumber)) out.push(`<ram:SpecifiedTaxRegistration><ram:ID schemeID="FC">${esc(clean(pt.taxNumber))}</ram:ID></ram:SpecifiedTaxRegistration>`);
  out.push(`</ram:${tag}>`);
  return out.join("");
}

/** BG-16 im CII-Schema: Art (BT-81), Anweisung (BT-82), Konto (BT-84 IBAN bzw. Kontonummer, BT-85), BIC (BT-86) */
function paymentMeansXml(m: EInvoicePaymentMeans): string {
  // Reihenfolge im CII-Schema (CreditorFinancialAccountType): IBANID, AccountName, ProprietaryID
  const name = m.accountName ? `<ram:AccountName>${esc(m.accountName)}</ram:AccountName>` : "";
  const account = m.iban ? `<ram:IBANID>${esc(m.iban)}</ram:IBANID>${name}` : m.accountId ? `${name}<ram:ProprietaryID>${esc(m.accountId)}</ram:ProprietaryID>` : "";
  return "<ram:SpecifiedTradeSettlementPaymentMeans>"
    + `<ram:TypeCode>${m.code}</ram:TypeCode>`
    + (m.text ? `<ram:Information>${esc(m.text)}</ram:Information>` : "")
    + (account ? `<ram:PayeePartyCreditorFinancialAccount>${account}</ram:PayeePartyCreditorFinancialAccount>` : "")
    + (account && m.bic ? `<ram:PayeeSpecifiedCreditorFinancialInstitution><ram:BICID>${esc(m.bic)}</ram:BICID></ram:PayeeSpecifiedCreditorFinancialInstitution>` : "")
    + "</ram:SpecifiedTradeSettlementPaymentMeans>";
}

const dateTime = (tag: string, iso: string, ns = "udt") => `<ram:${tag}><${ns}:DateTimeString format="102">${d102(iso)}</${ns}:DateTimeString></ram:${tag}>`;

/** CII-XML der E-Rechnung. Wirft, wenn checkEInvoice etwas meldet. */
export function buildEInvoiceXml(input: EInvoiceInput): string {
  const problems = checkEInvoice(input);
  if (problems.length) throw new Error(`E-Rechnung unvollständig: ${problems.map((p) => `${p.rule} ${p.text}`).join(" · ")}`);
  const breakdown = taxBreakdown(input);
  // „Nicht steuerbar“ (O) verbietet USt-IdNr. von Aussteller und Empfänger im Datensatz (BR-O-02)
  const omitVat = breakdown.some((b) => b.category === "O");
  const cur = esc(cleanId(input.currency));
  const s = docSign(input);
  const notes = [...(input.notes ?? []), ...(input.taxNotice ? [input.taxNotice] : [])].map(clean).filter(Boolean);
  const lines = input.lines.map((l, i) => {
    const category = taxCategoryFor(input.taxMode, l.ratePercent);
    // Betrag relativ zum Beleg; Preis nie negativ (BR-27), das Vorzeichen trägt die Menge (Rabatt: Menge −1, Preis positiv)
    const lineAmount = s * l.netAmountCents;
    const qty = Math.abs(l.quantity) * (lineAmount < 0 ? -1 : 1);
    return [
      "<ram:IncludedSupplyChainTradeLineItem>",
      `<ram:AssociatedDocumentLineDocument><ram:LineID>${i + 1}</ram:LineID></ram:AssociatedDocumentLineDocument>`,
      `<ram:SpecifiedTradeProduct><ram:Name>${esc(clean(l.name))}</ram:Name></ram:SpecifiedTradeProduct>`,
      `<ram:SpecifiedLineTradeAgreement><ram:NetPriceProductTradePrice><ram:ChargeAmount>${amount(Math.abs(l.netPriceCents))}</ram:ChargeAmount></ram:NetPriceProductTradePrice></ram:SpecifiedLineTradeAgreement>`,
      `<ram:SpecifiedLineTradeDelivery><ram:BilledQuantity unitCode="${esc(l.unitCode ?? "C62")}">${qty}</ram:BilledQuantity></ram:SpecifiedLineTradeDelivery>`,
      "<ram:SpecifiedLineTradeSettlement>",
      `<ram:ApplicableTradeTax><ram:TypeCode>VAT</ram:TypeCode><ram:CategoryCode>${category}</ram:CategoryCode>`
        + (category === "O" ? "" : `<ram:RateApplicablePercent>${rate(category === "S" ? l.ratePercent : 0)}</ram:RateApplicablePercent>`) + "</ram:ApplicableTradeTax>",
      `<ram:SpecifiedTradeSettlementLineMonetarySummation><ram:LineTotalAmount>${amount(lineAmount)}</ram:LineTotalAmount></ram:SpecifiedTradeSettlementLineMonetarySummation>`,
      "</ram:SpecifiedLineTradeSettlement>",
      "</ram:IncludedSupplyChainTradeLineItem>",
    ].join("");
  });
  const taxes = breakdown.map((b) => [
    "<ram:ApplicableTradeTax>",
    `<ram:CalculatedAmount>${amount(b.taxCents)}</ram:CalculatedAmount><ram:TypeCode>VAT</ram:TypeCode>`,
    b.category === "S" || b.category === "Z" ? "" : `<ram:ExemptionReason>${esc(clean(input.taxNotice) || EXEMPTION_DEFAULT[b.category])}</ram:ExemptionReason>`,
    `<ram:BasisAmount>${amount(b.basisCents)}</ram:BasisAmount><ram:CategoryCode>${b.category}</ram:CategoryCode>`,
    // BR-DE-14: XRechnung verlangt den Satz (BT-119) auch bei „nicht steuerbar“ (0)
    `<ram:RateApplicablePercent>${rate(b.category === "O" ? 0 : b.ratePercent)}</ram:RateApplicablePercent>`,
    "</ram:ApplicableTradeTax>",
  ].join(""));
  const means = paymentMeansOf(input);
  const prepaid = Math.abs(input.totals.prepaidCents ?? 0);
  const taxCur = input.taxCurrency && cleanId(input.taxCurrency.code) !== cleanId(input.currency) ? input.taxCurrency : null;
  const grand = s * input.totals.grossCents;
  const settlement = [
    "<ram:ApplicableHeaderTradeSettlement>",
    // BT-6 steht im CII-Schema vor der Rechnungswährung (BT-5)
    taxCur ? `<ram:TaxCurrencyCode>${esc(cleanId(taxCur.code))}</ram:TaxCurrencyCode>` : "",
    `<ram:InvoiceCurrencyCode>${cur}</ram:InvoiceCurrencyCode>`,
    means ? paymentMeansXml(means) : "",
    ...taxes,
    input.servicePeriod ? `<ram:BillingSpecifiedPeriod>${dateTime("StartDateTime", input.servicePeriod.from)}${dateTime("EndDateTime", input.servicePeriod.to)}</ram:BillingSpecifiedPeriod>` : "",
    input.dueDate || clean(input.paymentTerms)
      ? "<ram:SpecifiedTradePaymentTerms>" + (clean(input.paymentTerms) ? `<ram:Description>${esc(clean(input.paymentTerms))}</ram:Description>` : "")
        + (input.dueDate ? dateTime("DueDateDateTime", input.dueDate) : "") + "</ram:SpecifiedTradePaymentTerms>"
      : "",
    "<ram:SpecifiedTradeSettlementHeaderMonetarySummation>",
    `<ram:LineTotalAmount>${amount(s * input.totals.netCents)}</ram:LineTotalAmount>`,
    `<ram:TaxBasisTotalAmount>${amount(s * input.totals.netCents)}</ram:TaxBasisTotalAmount>`,
    `<ram:TaxTotalAmount currencyID="${cur}">${amount(s * input.totals.taxCents)}</ram:TaxTotalAmount>`,
    // BT-111: Gesamtsteuer in der Steuerwährung (zweites TaxTotalAmount)
    taxCur ? `<ram:TaxTotalAmount currencyID="${esc(cleanId(taxCur.code))}">${amount(s * taxCur.taxCents)}</ram:TaxTotalAmount>` : "",
    `<ram:GrandTotalAmount>${amount(grand)}</ram:GrandTotalAmount>`,
    prepaid ? `<ram:TotalPrepaidAmount>${amount(prepaid)}</ram:TotalPrepaidAmount>` : "",
    `<ram:DuePayableAmount>${amount(Math.max(0, grand - prepaid))}</ram:DuePayableAmount>`,
    "</ram:SpecifiedTradeSettlementHeaderMonetarySummation>",
    input.precedingInvoice?.number
      ? `<ram:InvoiceReferencedDocument><ram:IssuerAssignedID>${esc(clean(input.precedingInvoice.number))}</ram:IssuerAssignedID>`
        + (input.precedingInvoice.date ? dateTime("FormattedIssueDateTime", input.precedingInvoice.date, "qdt") : "") + "</ram:InvoiceReferencedDocument>"
      : "",
    "</ram:ApplicableHeaderTradeSettlement>",
  ];
  const buyerRef = clean(input.buyerReference);
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<rsm:CrossIndustryInvoice xmlns:rsm="urn:un:unece:uncefact:data:standard:CrossIndustryInvoice:100"'
      + ' xmlns:ram="urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100"'
      + ' xmlns:qdt="urn:un:unece:uncefact:data:standard:QualifiedDataType:100"'
      + ' xmlns:udt="urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100">',
    "<rsm:ExchangedDocumentContext>"
      + `<ram:BusinessProcessSpecifiedDocumentContextParameter><ram:ID>${PEPPOL_PROCESS}</ram:ID></ram:BusinessProcessSpecifiedDocumentContextParameter>`
      + `<ram:GuidelineSpecifiedDocumentContextParameter><ram:ID>${EINVOICE_GUIDELINE[input.profile]}</ram:ID></ram:GuidelineSpecifiedDocumentContextParameter>`
      + "</rsm:ExchangedDocumentContext>",
    "<rsm:ExchangedDocument>"
      + `<ram:ID>${esc(clean(input.number))}</ram:ID><ram:TypeCode>${input.typeCode}</ram:TypeCode>`
      + dateTime("IssueDateTime", input.issueDate)
      + notes.map((n) => `<ram:IncludedNote><ram:Content>${esc(n)}</ram:Content></ram:IncludedNote>`).join("")
      + "</rsm:ExchangedDocument>",
    "<rsm:SupplyChainTradeTransaction>",
    ...lines,
    "<ram:ApplicableHeaderTradeAgreement>"
      + (buyerRef ? `<ram:BuyerReference>${esc(buyerRef)}</ram:BuyerReference>` : "")
      + party("SellerTradeParty", input.seller, input.sellerContact, omitVat)
      + party("BuyerTradeParty", input.buyer, null, omitVat)
      + (clean(input.orderReference) ? `<ram:BuyerOrderReferencedDocument><ram:IssuerAssignedID>${esc(clean(input.orderReference))}</ram:IssuerAssignedID></ram:BuyerOrderReferencedDocument>` : "")
      + "</ram:ApplicableHeaderTradeAgreement>",
    "<ram:ApplicableHeaderTradeDelivery>"
      + (input.deliveryDate ? `<ram:ActualDeliverySupplyChainEvent>${dateTime("OccurrenceDateTime", input.deliveryDate)}</ram:ActualDeliverySupplyChainEvent>` : "")
      + "</ram:ApplicableHeaderTradeDelivery>",
    settlement.join(""),
    "</rsm:SupplyChainTradeTransaction>",
    "</rsm:CrossIndustryInvoice>",
  ].join("\n");
}

/** Dateiname der E-Rechnung: wie das PDF, mit .xml */
export function eInvoiceFileName(number: string, profile: EInvoiceProfile): string {
  const safe = clean(number).replace(/[^A-Za-z0-9._-]+/g, "-") || "rechnung";
  return `${safe}${profile === "xrechnung" ? "_xrechnung" : "_en16931"}.xml`;
}
